import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';

/**
 * The one channel every live view of "what this account may enter" listens to after a role grant is created or
 * revoked, anywhere in the app.
 *
 * ### Why this exists
 *
 * The context chooser tiles (`ChooseContextComponent`), "Ihre freigeschalteten Zugänge"
 * (`MyGrantsPanelComponent`) and the bottom-left context switcher (`ContextSwitcherComponent`) each fetch their
 * OWN copy of the grant list independently (`GET /auth/context/available` / `GET /grants/mine`) — there was no
 * shared refresh signal between them. Revoking a grant in the my-grants panel left the switcher (and the
 * chooser tiles, when both are mounted on the same page) showing the now-revoked access until a full page
 * reload; a self-grant issued in one panel did not appear as a new tile in a sibling panel mounted alongside
 * it either. This service is the fix: a bare "something changed, go re-ask the server" signal, so a mutation
 * in one component can refresh every other live view without them knowing about each other.
 *
 * ### Why a bare `Subject<void>`, not a shared cache
 *
 * This is deliberately NOT a cache of the grant list itself — each consumer still owns its own fetch, its own
 * loading/error state, and its own shape of the data (`SelectableContext[]` here, `SelfGrantSummary[]` there).
 * A shared cache would mean agreeing on one shape and one refresh policy for three screens that already each
 * have their own. This is only the doorbell; every listener decides for itself what "re-asking" means.
 *
 * ### The loop it must never become
 *
 * `notifyChanged()` is called only from the USER-triggered mutations (a revoke succeeding, a self-grant being
 * issued) — never from a `changed$` subscriber's own reload. A reload is read-only from this service's point
 * of view, so the signal can never re-trigger itself into a cascade.
 */
@Injectable({providedIn: 'root'})
export class GrantChangeNotifierService {
  private readonly subject = new Subject<void>();

  /** Emits once after a grant CREATE or REVOKE succeeds anywhere in the app. Never emitted by a reload. */
  public readonly changed$: Observable<void> = this.subject.asObservable();

  /** Tells every live subscriber the grant list changed, so it can re-fetch its own view of it. */
  public notifyChanged(): void {
    this.subject.next();
  }
}
